import { isValidElement, type ReactNode } from 'react'
import { Navigate, matchRoutes, type RouteObject } from 'react-router-dom'
import RedirectKeepingState from '../components/RedirectKeepingState'

/**
 * The route catalog: every page a link can open, read from the router's own
 * route config into a tree of address segments. The route picker builds a
 * page's address from fields along that tree, and reads an address back into
 * the same fields, so an address is all that is ever stored.
 *
 * It grows with the router. A route is in the catalog as soon as it is in the
 * router, unless
 * - it only redirects (its element is a `<Navigate>` or `<RedirectKeepingState>`),
 * - it, or a route above it, opts out with `handle: { routePicker: false }`, or
 * - it has a parameter with no source of values (see routeParamSources.ts).
 */

/** The parts of a router route object the catalog reads. */
export interface RouteConfigLike {
  path?: string
  index?: boolean
  element?: ReactNode
  handle?: unknown
  children?: RouteConfigLike[]
}

/** What a route's `handle` can tell the route picker. */
export interface RoutePickerHandle {
  routePicker?:
    | false
    | {
        /**
         * The choice that stays on this page when pages sit below it, such as
         * "All groups" for Permission Groups. `false` offers none, for a page
         * that only forwards (the tables index, to the first table), though
         * an address whose item is gone can still be cut back to it.
         */
        landing?: string | false
        /**
         * The fixed segment below whose items this page's own address stands
         * in for, such as `table-views` for a table (its address opens its
         * default view): the picker shows it there, as Views › Default, not
         * as a choice beside the page's own pages.
         */
        landingIn?: string
        /** Signed-out pages (Sign In, Sign Up): left out of the catalog, as their whole branch. */
        public?: true
      }
}

/** A page in the catalog: its address pattern, such as `/tables/:tableId/settings`. */
export interface CatalogPage {
  pattern: string
  /** A signed-out page (Sign In), offered only with `includePublic`. */
  isPublic?: true
  landing?: string | false
  landingIn?: string
}

export interface RouteNode {
  /** The address segment this node adds (`tables`, `:tableId`); empty at the root. */
  segment: string
  /** The address pattern down to here (`/tables/:tableId`). */
  pattern: string
  /** A page opens at exactly this address. */
  isPage: boolean
  /** The choice that stays on this page, when it has pages below it; `false` for none. */
  landing?: string | false
  /** The fixed segment below whose items this page's address stands in for. */
  landingIn?: string
  /** Fixed segments below this one, in route order. */
  statics: RouteNode[]
  /** The parameter below this one, if any. */
  param: RouteNode | null
}

/** One segment of an address, matched to the catalog. */
export interface RouteStep {
  /** The node whose children this step picks among. */
  parent: RouteNode
  node: RouteNode
  /** The address segment: a fixed segment itself, or a parameter's value. */
  value: string
}

export interface ParsedRoute {
  steps: RouteStep[]
  /** Every segment matched the catalog and the address is a page. */
  complete: boolean
}

/** A level of the picker: one choice among a node's children. */
export interface RouteField {
  /** How many steps come before this field. */
  depth: number
  /** The node whose children this field picks among. */
  node: RouteNode
  /** The step picked here, or null when nothing is picked yet. */
  step: RouteStep | null
  /** The parameters picked above this field. */
  params: RouteParams
  /** Something must be picked here; otherwise the address above is already a page. */
  required: boolean
  /**
   * The node's only child, a bare collection of items (a grid's `views`),
   * folded into this field: it picks an item straight from the page, and
   * `step` is the item's. Without one, the field picks among `node`'s children.
   */
  collection?: RouteNode
}

export type RouteParams = Readonly<Record<string, string>>

const ALIAS_ELEMENTS = new Set<unknown>([Navigate, RedirectKeepingState])

export function isParamSegment(segment: string) {
  return segment.startsWith(':')
}

function paramName(segment: string) {
  return segment.slice(1)
}

export function splitPath(path: string): string[] {
  const pathname = path.split(/[?#]/, 1)[0] ?? ''
  return pathname.split('/').filter(Boolean)
}

function joinPattern(parent: string, path: string) {
  const joined = path.startsWith('/') ? path : `${parent}/${path}`
  const segments = splitPath(joined)
  return segments.length > 0 ? `/${segments.join('/')}` : '/'
}

function routePickerHandle(route: RouteConfigLike): RoutePickerHandle['routePicker'] {
  const { handle } = route
  if (handle && typeof handle === 'object' && 'routePicker' in handle) {
    return (handle as RoutePickerHandle).routePicker
  }
  return undefined
}

/** Left out of the default catalog: opted out, or a signed-out page. */
function isLeftOutHandle(handle: RoutePickerHandle['routePicker']) {
  return handle === false || handle?.public === true
}

/**
 * The router opens `pathname` on a route left out of the catalog (`routePicker:
 * false` or a signed-out page, on it or above it), such as a new automation's
 * page, whose `new` the catalog would otherwise read as an automation's id.
 */
export function isRouteLeftOut(routes: readonly RouteConfigLike[], pathname: string): boolean {
  const matches = matchRoutes(routes as RouteObject[], pathname) ?? []
  return matches.some((match) => isLeftOutHandle(routePickerHandle(match.route as RouteConfigLike)))
}

function isAlias(element: ReactNode) {
  return isValidElement(element) && ALIAS_ELEMENTS.has(element.type)
}

/**
 * Every page in a router config, in route order. Signed-out pages only with
 * ``includePublic`` (an email's link to Sign In or Reset Password), where a
 * page that takes the rest of its address (`/sign-in/*`) is its own address.
 */
export function collectCatalogPages(
  routes: readonly RouteConfigLike[],
  parentPattern = '/',
  { includePublic = false, inPublic = false }: { includePublic?: boolean; inPublic?: boolean } = {}
): CatalogPage[] {
  const pages: CatalogPage[] = []
  for (const route of routes) {
    const handle = routePickerHandle(route)
    // Opted out, or signed out: the whole branch.
    if (handle === false || (handle?.public === true && !includePublic)) continue
    const isPublic = inPublic || handle?.public === true

    let pattern = route.index
      ? parentPattern
      : route.path !== undefined
        ? joinPattern(parentPattern, route.path)
        : parentPattern
    if (isPublic && pattern.endsWith('/*')) pattern = pattern.slice(0, -2) || '/'
    // A splat can't be built from fields.
    if (pattern.includes('*')) continue

    if (route.children && route.children.length > 0) {
      // A layout: its own address is a page only through an index child.
      pages.push(
        ...collectCatalogPages(route.children, pattern, { includePublic, inPublic: isPublic })
      )
      continue
    }
    if (route.path === undefined && !route.index) continue
    if (isAlias(route.element)) continue
    pages.push({
      pattern,
      ...(isPublic ? { isPublic: true } : {}),
      ...(handle?.landing !== undefined ? { landing: handle.landing } : {}),
      ...(handle?.landingIn ? { landingIn: handle.landingIn } : {}),
    })
  }
  return pages
}

function createNode(segment: string, pattern: string): RouteNode {
  return { segment, pattern, isPage: false, statics: [], param: null }
}

/**
 * The catalog tree for these pages. A page whose parameters don't all have a
 * source (`hasParamSource`) is left out, and so is one whose parameter would
 * sit beside a differently named one at the same level.
 */
export function buildRouteCatalog(
  pages: readonly CatalogPage[],
  hasParamSource: (paramPattern: string) => boolean
): RouteNode {
  const root = createNode('', '/')

  for (const page of pages) {
    const segments = splitPath(page.pattern)

    // Check the whole path before adding any of it.
    let probe: RouteNode | null = root
    let probePattern = ''
    let usable = true
    for (const segment of segments) {
      probePattern = `${probePattern}/${segment}`
      if (isParamSegment(segment)) {
        if (!hasParamSource(probePattern) || (probe?.param && probe.param.segment !== segment)) {
          usable = false
          break
        }
        probe = probe?.param ?? null
      } else {
        probe = probe?.statics.find((child) => child.segment === segment) ?? null
      }
    }
    if (!usable) continue

    let current = root
    for (const segment of segments) {
      const pattern = joinPattern(current.pattern, segment)
      if (isParamSegment(segment)) {
        current.param ??= createNode(segment, pattern)
        current = current.param
      } else {
        let child = current.statics.find((candidate) => candidate.segment === segment)
        if (!child) {
          child = createNode(segment, pattern)
          current.statics.push(child)
        }
        current = child
      }
    }
    current.isPage = true
    if (page.landing !== undefined) current.landing = page.landing
    if (page.landingIn) current.landingIn = page.landingIn
  }

  return root
}

/**
 * A level the picker skips: not a page, with exactly one fixed segment below
 * it (`table-views` before a view), so there is nothing to choose.
 */
function isPassThrough(node: RouteNode) {
  return !node.isPage && node.param === null && node.statics.length === 1
}

/**
 * A page's only child when it is a bare collection of items (a grid page's
 * `views`, holding `:viewId`): not a page, nothing fixed under it, just the
 * items. Picking it is picking an item, so the picker folds it into the page.
 */
export function foldedCollection(node: RouteNode): RouteNode | null {
  if (!node.isPage || node.param !== null || node.statics.length !== 1) return null
  const [child] = node.statics
  return !child.isPage && child.statics.length === 0 && child.param !== null ? child : null
}

/**
 * An address matched against the catalog. A fixed segment wins over a
 * parameter, as in the router. Stops at the first segment it can't match.
 */
export function parseRoutePath(root: RouteNode, path: string): ParsedRoute {
  const segments = splitPath(path)
  const steps: RouteStep[] = []
  let current = root
  for (const segment of segments) {
    const child =
      current.statics.find((candidate) => candidate.segment === segment) ?? current.param
    if (!child) return { steps, complete: false }
    steps.push({ parent: current, node: child, value: segment })
    current = child
  }
  return { steps, complete: current.isPage }
}

/** The address these steps make. */
export function routePathFromSteps(steps: readonly RouteStep[]) {
  return steps.length > 0 ? `/${steps.map((step) => step.value).join('/')}` : '/'
}

/** The parameters these steps set, by name. */
export function routeParamsFromSteps(steps: readonly RouteStep[]): RouteParams {
  const params: Record<string, string> = {}
  for (const step of steps) {
    if (isParamSegment(step.node.segment)) params[paramName(step.node.segment)] = step.value
  }
  return params
}

function lastRouteNode(root: RouteNode, steps: readonly RouteStep[]) {
  return steps.length > 0 ? steps[steps.length - 1].node : root
}

/**
 * The address is a page the picker can end on as it stands: not one that
 * offers no stay (the tables index, which only forwards to the first table).
 */
export function isCompleteRoute(root: RouteNode, steps: readonly RouteStep[]) {
  const last = lastRouteNode(root, steps)
  return last.isPage && last.landing !== false
}

/**
 * The steps as the picker shows them: ending on a page whose own address
 * stands in below it (a table, whose address opens its default view), they
 * go on to the level it stands in (Views), where nothing is picked: the page
 * itself is that level's stand-in (Default). Otherwise as they are.
 */
export function routeStepsWithLanding(root: RouteNode, steps: readonly RouteStep[]): RouteStep[] {
  const last = lastRouteNode(root, steps)
  const via = last.isPage
    ? last.statics.find((child) => child.segment === last.landingIn)
    : undefined
  return via ? [...steps, { parent: last, node: via, value: via.segment }] : [...steps]
}

/**
 * Steps carried down while the address isn't a page yet and the level below
 * offers only fixed segments: the first is taken (a view's Records before its
 * Settings, an area's Overview), so a pick leaves a working address wherever
 * it can. A level that also offers a parameter waits for the user.
 */
function extendRouteSteps(root: RouteNode, steps: readonly RouteStep[]): RouteStep[] {
  const extended = [...steps]
  let current = lastRouteNode(root, extended)
  while (!current.isPage && current.param === null && current.statics.length > 0) {
    const next = current.statics[0]
    extended.push({ parent: current, node: next, value: next.segment })
    current = next
  }
  return extended
}

/**
 * The steps with a different pick at one level: everything below it is
 * dropped, and levels with nothing to choose are filled in.
 */
export function chooseRouteStep(
  root: RouteNode,
  steps: readonly RouteStep[],
  depth: number,
  pick: { node: RouteNode; value: string } | null
): RouteStep[] {
  const kept = steps.slice(0, depth)
  if (!pick) return kept
  const parent = lastRouteNode(root, kept)
  return extendRouteSteps(root, [...kept, { parent, node: pick.node, value: pick.value }])
}

/** The picker's levels for these steps, down to the first one with nothing picked. */
export function routeFields(root: RouteNode, steps: readonly RouteStep[]): RouteField[] {
  const fields: RouteField[] = []
  let current = root
  let depth = 0
  const params: Record<string, string> = {}

  while (current.statics.length > 0 || current.param) {
    const step = steps[depth] ?? null
    const collection = foldedCollection(current)
    if (collection) {
      // One field for the collection's segment and its item, two steps.
      const itemStep = step ? (steps[depth + 1] ?? null) : null
      fields.push({
        depth,
        node: current,
        step: itemStep,
        params: { ...params },
        required: false,
        collection,
      })
      if (!itemStep) break
      params[paramName(itemStep.node.segment)] = itemStep.value
      current = itemStep.node
      depth += 2
      continue
    }
    if (!isPassThrough(current)) {
      // A page that offers no choice to stay on it, or stands in below (a
      // table, as its Views › Default), isn't a choice at its own level.
      const required =
        !current.isPage || current.landing === false || current.landingIn !== undefined
      fields.push({ depth, node: current, step, params: { ...params }, required })
    }
    if (!step) break
    if (isParamSegment(step.node.segment)) params[paramName(step.node.segment)] = step.value
    current = step.node
    depth += 1
  }

  return fields
}

/** A fixed segment as a name: `table-views` reads "Table Views" unless named otherwise. */
export function titleCaseSegment(segment: string) {
  return segment
    .split(/[-_]/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

export interface ResolveRouteDeps {
  /**
   * The values a parameter can take for the signed-in user, or null when they
   * can't be had (offline, an error), in which case the address is trusted.
   */
  loadParamValues: (paramNode: RouteNode, params: RouteParams) => Promise<readonly string[] | null>
  /** The signed-in user can open this top-level area (Tables, Integrations). */
  isTopLevelAvailable: (segment: string) => boolean
}

export interface ResolvedRoute {
  path: string
  /** The address no longer leads where it did and was cut back. */
  changed: boolean
}

/**
 * Where a stored address leads now. A parameter whose item is gone (a
 * deleted view, a table the user lost access to), an area that is switched
 * off, or a segment the app no longer has, cuts the address back to the
 * nearest page above it: a deleted view falls back to its table's default
 * view, a deleted table to the first table, and so on up to the app's home.
 */
export async function resolveRoutePath(
  root: RouteNode,
  path: string,
  deps: ResolveRouteDeps
): Promise<ResolvedRoute> {
  const parsed = parseRoutePath(root, path)
  let keep = parsed.steps.length

  for (let index = 0; index < parsed.steps.length; index += 1) {
    const step = parsed.steps[index]
    if (!isParamSegment(step.node.segment)) {
      if (index === 0 && !deps.isTopLevelAvailable(step.value)) {
        keep = 0
        break
      }
      continue
    }
    const values = await deps.loadParamValues(
      step.node,
      routeParamsFromSteps(parsed.steps.slice(0, index))
    )
    if (values && !values.includes(step.value)) {
      keep = index
      break
    }
  }

  const steps = parsed.steps.slice(0, keep)
  while (steps.length > 0 && !steps[steps.length - 1].node.isPage) steps.pop()
  const resolvedPath = routePathFromSteps(steps)
  const requestedPath = `/${splitPath(path).join('/')}`
  return { path: resolvedPath, changed: resolvedPath !== requestedPath }
}
